// Schnell-Einstellungen — Theme · Sprache · Währung.
//
// Zwei Exporte:
//  • SettingSwitch — moderner iOS-Schiebeschalter (gleitender weißer Knopf,
//    aktive Spur = mattes Marken-Rot #B33E3A wie der bestehende Toggle). Die
//    zwei Optionen flankieren den Schalter (links/rechts) — so bleibt bei
//    Nicht-on/off-Werten (DE/EN, EUR/USD) eindeutig, was welche Seite ist.
//    Wird in der Profil-Einstellungskarte UND im Header-Menü verwendet.
//  • QuickSettingsMenu (default) — Chevron-Pfeil im Desktop-Header (rechts
//    neben der Glocke), öffnet ein Dropdown mit allen drei Schaltern. So lässt
//    sich Theme/Sprache/Währung umstellen, ohne ins Profil zu wechseln.
//    NUR Webapp/Desktop (in DesktopShell eingebunden, nicht in der Mobile-Nav).

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useProfile } from '../../context/ProfileContext';
import { usePrivacy } from '../../context/PrivacyContext';
import { useT } from '../../i18n/useT';
import { F, EASE } from '../../styles/theme';
import { Icon, Icons } from './Icons';

const SWITCH_ON = '#E4544A'; // Marken-Koralle (aktive Schalter-Spur)

// ── SettingSwitch — reiner iOS-Schiebeschalter ───────────────────────────────
// options: [offOption, onOption], je { value, l }. „on" = rechte Option.
// Feste Breite → rechtsbündig fluchten alle Schalter untereinander.
export function SettingSwitch({ value, onChange, options }) {
  const [off, on] = options;
  const isOn = value === on.value;
  return (
    <button
      role="switch"
      aria-checked={isOn}
      aria-label={isOn ? on.l : off.l}
      onClick={() => onChange(isOn ? off.value : on.value)}
      className="red-toggle"
      style={{
        width: 42, height: 25, borderRadius: 999, border: 'none', padding: 0,
        background: isOn ? SWITCH_ON : 'rgba(150,150,160,0.3)',
        position: 'relative', cursor: 'pointer', flexShrink: 0,
        transition: 'background 0.2s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: isOn ? 19 : 2,
        width: 21, height: 21, borderRadius: '50%', background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s ease',
      }} />
    </button>
  );
}

// ── OptionPair — „A / B"-Begriffspaar links neben dem Schalter ────────────────
// Big-Company-Muster (iOS/Google): die zwei Zustände als Text, der aktive
// hervorgehoben (Ink/fett), der inaktive blass. Rechts dann der reine Schalter.
export function OptionPair({ options, value }) {
  const { T } = useTheme();
  const [off, on] = options;
  const isOn = value === on.value;
  return (
    <span style={{ fontFamily: F.family, fontSize: 13, whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
      <span style={{ fontWeight: isOn ? 600 : 700, color: isOn ? T.mute : T.text, transition: 'color 0.2s ease' }}>{off.l}</span>
      <span style={{ color: T.mute, fontWeight: 600, margin: '0 3px' }}>/</span>
      <span style={{ fontWeight: isOn ? 700 : 600, color: isOn ? T.text : T.mute, transition: 'color 0.2s ease' }}>{on.l}</span>
    </span>
  );
}

// ── QuickSettingsMenu — Chevron-Trigger + Dropdown (nur Desktop-Header) ───────
export default function QuickSettingsMenu() {
  const { T, isDark, setIsDark } = useTheme();
  const { currency, setCurrency } = useCurrency();
  const { setLanguage } = useProfile();
  const { privateMode, setPrivateMode } = usePrivacy();
  const { t, lang } = useT();
  const reduce = useReducedMotion();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const cur = currency === 'USD' ? 'USD' : 'EUR';

  const rows = [
    {
      label: t('theme'),
      d: Icons.themeMoon,
      value: isDark ? 'dark' : 'light',
      onChange: (v) => setIsDark(v === 'dark'),
      options: [
        { value: 'light', l: lang === 'en' ? 'Day' : 'Tag' },
        { value: 'dark', l: lang === 'en' ? 'Night' : 'Nacht' },
      ],
    },
    {
      label: t('language'),
      d: Icons.language,
      value: lang === 'en' ? 'en' : 'de',
      onChange: setLanguage,
      options: [
        { value: 'de', l: 'Deutsch' },
        { value: 'en', l: lang === 'en' ? 'English' : 'Englisch' },
      ],
    },
    {
      label: t('currency'),
      d: Icons.currency,
      value: cur,
      onChange: setCurrency,
      options: [
        { value: 'EUR', l: 'EUR' },
        { value: 'USD', l: 'USD' },
      ],
    },
    {
      label: t('private_mode'),
      d: Icons.eyeOff,
      value: privateMode ? 'on' : 'off',
      onChange: (v) => setPrivateMode(v === 'on'),
      options: [
        { value: 'off', l: t('private_visible') },
        { value: 'on', l: t('private_hidden') },
      ],
    },
  ];

  const panelVariants = reduce
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.12 } },
        exit: { opacity: 0, transition: { duration: 0.1 } },
      }
    : {
        hidden: { opacity: 0, y: -12, scaleY: 0.85, scaleX: 0.98 },
        show: {
          opacity: 1, y: 0, scaleY: 1, scaleX: 1,
          transition: {
            type: 'spring', stiffness: 470, damping: 34, mass: 0.9,
            opacity: { duration: 0.15, ease: 'easeOut' },
          },
        },
        exit: { opacity: 0, y: -8, scaleY: 0.92, transition: { duration: 0.13, ease: [0.4, 0, 1, 1] } },
      };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t('settings')}
        aria-expanded={open}
        title={t('settings')}
        style={{
          width: 38, height: 38,
          background: 'transparent', border: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: open ? T.text : T.sub, padding: 0,
          transition: `color 0.15s ease, transform 0.14s ${EASE}`,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T.text; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = open ? T.text : T.sub; }}
      >
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 30 }}
          style={{ display: 'inline-flex' }}
        >
          <Icon d={Icons.chevD} size={18} stroke="currentColor" sw={2.4} />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="quick-settings"
            role="menu"
            variants={panelVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            style={{
              // Wie das Glocken-Panel: FIX am rechten Viewport-Rand, direkt
              // unter der Kopfzeile (~74px) — bündig zu den Alert-Benachrichtigungen.
              position: 'fixed', top: 80, right: 24,
              zIndex: 9998, transformOrigin: 'top right',
              width: 250, maxWidth: 'calc(100vw - 48px)',
              background: T.surface, border: `1px solid ${T.line}`,
              borderRadius: 16, overflow: 'hidden',
              boxShadow: isDark
                ? '0 18px 48px -14px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
                : '0 18px 44px -14px rgba(60,48,28,0.26), 0 3px 10px rgba(60,48,28,0.08)',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '14px 16px 12px',
              borderBottom: `1px solid ${T.line}`,
            }}>
              <span style={{
                flex: 1, fontFamily: F.display, fontSize: 14.5, fontWeight: 800,
                letterSpacing: '-0.02em', color: T.text,
              }}>{t('settings')}</span>
            </div>

            {/* Schalter-Reihen */}
            <div style={{ padding: '4px 16px 12px' }}>
              {rows.map((row, i, arr) => (
                <div
                  key={row.label}
                  title={row.label}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    padding: '13px 0',
                    borderBottom: i === arr.length - 1 ? 'none' : `1px solid ${T.lineSoft || T.line}`,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                    <Icon d={row.d} size={18} stroke={T.sub} sw={1.8} />
                    <OptionPair options={row.options} value={row.value} />
                  </span>
                  <SettingSwitch value={row.value} onChange={row.onChange} options={row.options} />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
